/* eslint-disable */
import eventBus from './eventBus';
import integrationsList from './integrationsList';

let gcmConfig = {};
let progress = 0;
let progressInterval = null;
let port1;
const { prefix } = integrationsList.list.filter((integration) => integration.name === 'LnW')[0];
const eventsWaitingForResponse = new Map();

/**
 * @typedef {Object} EventWaiterOptions
 * @property {number} [timeoutMs] Optional timeout in milliseconds.
 */

/**
 * Internal helper: create a deferred promise.
 *
 * @returns {{ promise: Promise<any>, resolve: (value: any) => void, reject: (reason?: any) => void }}
 */
const createDeferred = () => {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
};

/**
 * Waits for a single event with the given name to arrive via gcmMsgReceived.
 * Optionally accepts a timeout in milliseconds.
 *
 * @param {string} eventName
 * @param {EventWaiterOptions} [options]
 * @returns {Promise<any>} resolves with eventData for that event
 */
const waitForEvent = (eventName, { timeoutMs } = {}) => {
  const deferred = createDeferred();

  const waiter = {
    resolve: deferred.resolve,
    reject: deferred.reject,
    timeoutId: null,
  };

  if (!eventsWaitingForResponse.has(eventName)) {
    eventsWaitingForResponse.set(eventName, []);
  }
  eventsWaitingForResponse.get(eventName).push(waiter);

  if (typeof timeoutMs === 'number') {
    waiter.timeoutId = setTimeout(() => {
      const list = eventsWaitingForResponse.get(eventName) || [];
      const remaining = list.filter((w) => w !== waiter);

      if (remaining.length > 0) {
        eventsWaitingForResponse.set(eventName, remaining);
      } else {
        eventsWaitingForResponse.delete(eventName);
      }

      waiter.reject(new Error(`[GCM-LnW] Timeout waiting for event: ${eventName}`));
    }, timeoutMs);
  }

  return deferred.promise;
};

/**
 * Internal: notify all waiters listening for this eventName.
 *
 * @param {string} eventName
 * @param {any} eventData
 */
const notifyWaiters = (eventName, eventData) => {
  const list = eventsWaitingForResponse.get(eventName);
  if (!list || list.length === 0) return;

  list.forEach((waiter) => {
    const { resolve, timeoutId } = waiter;

    if (timeoutId) clearTimeout(timeoutId);
    resolve(eventData);
    // one-shot: don't re-add this waiter to remaining
  });

  // no waiters left for this event name
  eventsWaitingForResponse.delete(eventName);
};

const setGCMConfig = (config) => {
  gcmConfig = config;
};

const sendMsgToGcm = (msg) => {
  if (!port1) {
    console.warn('[GCM-LnW] Tried to send message before port was initialised:', msg);
    return;
  }
  console.log('[GCM-LnW] Sending message to gcm:', msg);
  port1.postMessage(JSON.stringify(msg));
};

const startProgressUpdate = () => {
  if (progressInterval) {
    clearInterval(progressInterval);
  }

  progress = 0;
  progressInterval = setInterval(() => {
    progress += 1;
    const message = {
      eventName: `${prefix}loadProgressUpdate`,
      eventData: {
        percent: progress,
      },
    };
    if (progress >= 99) {
      clearInterval(progressInterval);
      progressInterval = null;
    }
    sendMsgToGcm(message);
  }, 200);
};

const handleSettingChanged = (setting, value, changedFrom) => {
  const message = {
    eventName: `${prefix}optionHasChanged`,
    eventData: {
      optionType: setting,
      newValue: value,
      changedFrom,
    },
  };
  sendMsgToGcm(message);
};

/**
 * Register a UI option with GCM.
 *
 * @param {String} option - One of: "MUTE", "ABOUT", "HELP", "PAYTABLE".
 * @returns {void}
 *
 * @example
 * registerOptions('MUTE');
 */
const registerOption = (option) => {
  const message = {
    eventName: `${prefix}regOption`,
    eventData: {
      optionType: option,
    },
  };
  sendMsgToGcm(message);
};
/**
 * Representing current STAKE/PAYIN
 *
 * @param {Number} amount
 * @returns {void}
 */
const handleStakeUpdate = (amount) => {
  const message = {
    eventName: `${prefix}stakeUpdate`,
    eventData: {
      amount,
    },
  };
  sendMsgToGcm(message);
};
/**
 * Representing last WIN AMOUNT
 *
 * @param {Number} amount
 * @returns {void}
 */
const handlePaidUpdate = (amount) => {
  const message = {
    eventName: `${prefix}paidUpdate`,
    eventData: {
      amount,
    },
  };
  sendMsgToGcm(message);
};
const handleGameAnimationStart = () => {
  const message = {
    eventName: `${prefix}gameAnimationStart`,
  };
  sendMsgToGcm(message);
};

const handleGameAnimationComplete = () => {
  const message = {
    eventName: `${prefix}gameAnimationComplete`,
  };
  sendMsgToGcm(message);
};

const handleSetOGSParams = (data) => {
  console.log('[GCM-LnW] handle set ogs params', data);
  // call set game config
  const { cuiPosition, gameLoadingScreen, gameName } = gcmConfig;

  const gameConfigEvent = {
    eventName: `${prefix}setGameConfig`,
    eventData: {
      cuiPosition,
      gameLoadingScreen,
      gameName,
    },
  };
  sendMsgToGcm(gameConfigEvent);
  startProgressUpdate();
};

const handleGameReady = () => {
  clearInterval(progressInterval);
  progressInterval = null;
  const message = {
    eventName: `${prefix}gameReady`,
  };
  sendMsgToGcm(message);
};

const gcmMsgReceived = async (msg) => {
  if (!msg) return;
  let parsed;
  try {
    parsed = typeof msg.data === 'string' ? JSON.parse(msg.data) : msg.data;
  } catch (error) {
    console.error('[GCM-LnW] Failed to parse GCM message:', msg.data, error);
    return;
  }

  console.log('[GCM-LnW] Parsed message from gcm:', parsed);
  const { eventName, eventData } = parsed || {};

  notifyWaiters(eventName, eventData);

  switch (eventName) {
    case `${prefix}setOGSParams`:
      handleSetOGSParams(eventData);
      break;
    case `${prefix}gameRevealed`:
      // add logic if needed
      break;
    case `${prefix}paidUpdateResponse`:
      // add logic if needed
      break;
    case `${prefix}optionHasChanged`:
      if (eventData.option === 'MUTE') {
        eventBus.$emit('updateSoundSettings', !eventData.value);
      } else if (eventData.option === 'HELP') {
        if (eventData.value) {
          eventBus.$emit('openHelp');
        } else {
          eventBus.$emit('closeHelp');
        }
      }
      break;
    case `${prefix}resume`:
      eventBus.$emit('hideOverlay');
      break;
    default:
      console.warn('[GCM-LnW] Unknown event from gcm:', eventName, 'eventData:', eventData);
      break;
  }
};
/**
 * Representing current balance
 *
 * @param {Number} balance
 * @returns {void}
 */
const handleBalanceUpdate = (balance) => {
  const message = {
    eventName: `${prefix}balancesUpdate`,
    eventData: {
      balances: {
        CASH: Number(balance),
      },
    },
  };
  sendMsgToGcm(message);
};

const initGCM = async () => {
  const channel = new MessageChannel();
  const pmInitObj = {
    eventName: `${prefix}initialiseChannel`,
  };
  window.parent.postMessage(pmInitObj, '*', [channel.port2]);
  port1 = channel.port1;
  port1.onmessage = gcmMsgReceived;
};
/**
 * @typedef {'NON_RECOVERABLE_ERROR'|'CRITICAL'|'INSUFFICIENT_FUNDS'} ErrorCategory
 * @typedef {'WARNING'|'INFO'|'ERROR'} ErrorSeverity
 */
/**
 *
 * @param {ErrorCategory} category The category of error, e.g. NON_RECOVERABLE_ERROR, CRITICAL, INSUFFICIENT_FUNDS
 * @param {ErrorSeverity} severity Changes dialog title, valid options: WARNING, INFO, ERROR
 * @param {string} errorCode Code from GPAPI rc e.g.: 1, 1000
 * @param {string} message The error message, e.g., "A technical error has occurred."
 * @param {*} errorParams
 */
const handleError = (category, severity, errorCode, message, errorParams = '') => {
  const eventData = {
    category,
    severity,
    errorCode,
    message,
    errorParams,
  };
  const msg = {
    eventName: `${prefix}handleError`,
    eventData,
  };
  sendMsgToGcm(msg);
};

/**
 * @typedef {'left'|'right'|'center'} CuiPosition
 * @typedef {''|'game-a'|'game-b'} GameName
 */

/**
 * Initializes GCM communication. Takes init config object.
 *
 * @param {Object} [config]                             Init config object.
 * @param {CuiPosition} [config.cuiPosition='center']   Where to place the CUI.
 * @param {boolean} [config.gameLoadingScreen=true]     Show a loading screen.
 * @param {GameName} [config.gameName='']               Window title name.
 * @returns {void}
 */
const initCommunication = async ({ cuiPosition = 'center', gameLoadingScreen = true, gameName = '' } = {}) => {
  setGCMConfig({ cuiPosition, gameLoadingScreen, gameName });
  initGCM();
  await waitForEvent(`${prefix}setOGSParams`, {
    timeoutMs: 15000,
  });
};
export {
  initCommunication,
  handleGameReady,
  registerOption,
  handleBalanceUpdate,
  handlePaidUpdate,
  handleStakeUpdate,
  handleGameAnimationStart,
  handleGameAnimationComplete,
  handleSettingChanged,
  handleError,
};
